import AES from '../../../NonReact/security/AES'
import Base64 from '../../../NonReact/security/Base64'
import Utf8 from '../../../NonReact/security/Utf8'
import { IsStringEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { getNonce } from '../../../NonReact/common/DataIngest'

/**
 * A method that encrypts any string value and returns a AES encrypted string.
 * @param password A password string to be encrypted.
 * @param nonce The nonce from the user's session
 */
export const encrypt = (password?: Nullable<string>, nonce?: Nullable<string>): string => {
    if (!IsStringEmpty(password)) {
        // Character encode the password string.
        const encodedPassword: string = Utf8.encode(password)
        // Character encode the nonce string.
        const encodedNonce: string = Utf8.encode(nonce ?? getNonce())
        // Get nonce string length.
        const nonceLength: number = encodedNonce?.length
        // Max array size as a constant.
        const MAX_ARRAY_SIZE = 16
        // Create an empty 16 bit array.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const bitArr: number[] = new Array(MAX_ARRAY_SIZE)

        // Loop over the length of the array and apply a character from the
        // encoded nonce into the indexed place of the array.
        for (let i = 0; i < MAX_ARRAY_SIZE; i++) {
            if (i < nonceLength) bitArr[i] = encodedNonce.charCodeAt(i)
            else bitArr[i] = 0
        }

        // Expand the bit array using AES.
        const expandedKey: number[][] = AES.keyExpansion(bitArr)

        // If the encoded password is not empty then manipulate the string and
        // return a string that is Base64 encoded.
        if (!IsStringEmpty(encodedPassword) && !IsStringEmpty(encodedNonce)) {
            const encodedPasswordLength: number = encodedPassword.length
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const passwordBitArray: number[] = new Array(encodedPasswordLength)

            for (let i = 0; i < encodedPasswordLength; i++) {
                passwordBitArray[i] = encodedPassword.charCodeAt(i)
            }

            let cipheredPassword: number[] = []

            for (let i = 0; i < encodedPasswordLength / MAX_ARRAY_SIZE; i++) {
                cipheredPassword = cipheredPassword.concat(
                    AES.cipher(passwordBitArray.slice(i * MAX_ARRAY_SIZE, (i + 1) * MAX_ARRAY_SIZE), expandedKey)
                )
            }

            let encodedPasswordString: string = ''

            for (let i = 0; i < cipheredPassword.length; i++) {
                encodedPasswordString += String.fromCharCode(cipheredPassword[i])
            }

            // Encode the password string and return it in Base64.
            return Base64.encode(encodedPasswordString)
        }
    }

    // If the password string returns empty we return with an empty string.
    return ''
}
